import Tippy from '@tippyjs/react';
import PropTypes from 'prop-types';
import React from 'react';

import { formatNumber } from '../utils';
import useSmartSuggestions from '../useSmartSuggestions';
import NameCell from './NameCell';
import StatusPill from './StatusPill';
import SuggestionsLoading from './SuggestionsLoading';

// Mirrors ArtistTable's grid so suggestions read as the same table as All artists.
export const COLUMNS = 'minmax(220px, 2fr) minmax(220px, 3fr) minmax(90px, 1fr) minmax(96px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr)';
const HEADERS = [
  { label: 'Artist' },
  { label: 'Why this artist' },
  { label: 'Routing' },
  { label: 'Last local' },
  { label: 'Avg cap 12mo', num: true },
  { label: 'Spotify', num: true },
];

const ReasonChip = ({ reason }) => {
  const tooltip = reason.tooltip;
  if (tooltip && tooltip.length) {
    return (
      <Tippy
        content={
          <div className="ss-reason-tip">
            <div className="ss-reason-tip__title">Comparable venues played</div>
            <ul className="ss-reason-tip__list">
              {tooltip.map((venue) => <li key={venue}>{venue}</li>)}
            </ul>
          </div>
        }
      >
        <span className="ss-reason ss-reason--more" tabIndex={0}>{reason.text}</span>
      </Tippy>
    );
  }
  return <span className="ss-reason">{reason.text}</span>;
};
ReasonChip.propTypes = { reason: PropTypes.object.isRequired };

const SuggestionRow = ({ row }) => (
  <div className="ab-tr" style={{ gridTemplateColumns: COLUMNS }}>
    <div className="ab-td"><NameCell row={row} /></div>
    <div className="ab-td">
      <div className="ss-reasons">
        {row.reasons.map((reason, i) => <ReasonChip key={`${row.id}-${i}`} reason={reason} />)}
      </div>
    </div>
    <div className="ab-td"><StatusPill onTour={row.on_tour} /></div>
    <div className="ab-td">{row.last_local || '—'}</div>
    <div className="ab-td ab-td--num"><span className="ab-num">{formatNumber(row.avg_cap)}</span></div>
    <div className="ab-td ab-td--num"><span className="ab-num">{formatNumber(row.spotify)}</span></div>
  </div>
);
SuggestionRow.propTypes = { row: PropTypes.object.isRequired };

const SmartSuggestions = ({ suggestionsUrl, filters }) => {
  const { data, loading, loadingMore, error, loadMore, venueOwnershipId, setVenueOwnershipId } = useSmartSuggestions(suggestionsUrl, filters);

  if (loading && !data) return <SuggestionsLoading />;
  if (error && !data) return <div className="ss"><div className="ab-empty ab-empty--table">Could not load suggestions.</div></div>;
  if (!data) return null;

  const venueName = data.venue ? data.venue.name : 'your venue';

  return (
    <div className="ss">
      <div className="ss-header">
        <div className="ss-title">{formatNumber(data.total)} suggested for {venueName}</div>
        <div className="ss-sub">Narrowed by comparable venues and booking history.</div>
        {data.venues && data.venues.length > 1 && (
          <select
            className="ss-venue"
            value={venueOwnershipId ?? data.selected_venue_ownership_id ?? ''}
            onChange={(e) => setVenueOwnershipId(e.target.value ? Number(e.target.value) : null)}
            aria-label="Venue"
          >
            {data.venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
      </div>

      {data.groups.map((group) => (
        <div className="ss-group" key={group.key}>
          <div className="ss-group-head">
            <span className="ss-group-label">{group.label}</span>
            <span className="ss-group-count">{group.count}</span>
            {group.subtitle && <span className="ss-group-sub">{group.subtitle}</span>}
          </div>
          {group.rows.length === 0 ? (
            <div className="ab-empty ab-empty--table">No suggestions for this venue yet.</div>
          ) : (
            <div className="ss-table">
              <div className="ab-table__head" style={{ gridTemplateColumns: COLUMNS }}>
                {HEADERS.map((h) => (
                  <div key={h.label} className={`ab-th ${h.num ? 'ab-th--num' : ''}`}>{h.label}</div>
                ))}
              </div>
              {group.rows.map((row) => <SuggestionRow key={row.id} row={row} />)}
              {group.count > group.rows.length && (
                <div className="ss-more">
                  <span>Showing {formatNumber(group.rows.length)} of {formatNumber(group.count)}</span>
                  {group.has_more && (
                    <button type="button" className="ss-loadmore" onClick={loadMore} disabled={loadingMore}>
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
SmartSuggestions.propTypes = {
  suggestionsUrl: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
};

export default SmartSuggestions;
